import { useEffect, useMemo, useState } from "react";
import { Clock, ImageIcon } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/utils/cn";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import { upperLocacaoText } from "./upperLocacaoText";
import {
  REGISTRO_HORIMETRO_LABELS,
  deleteRegistroHorimetroFoto,
  extractApiError,
  fetchHorimetroEstado,
  registroHorimetroFotoUrl,
  uploadRegistroHorimetroFoto,
  type HorimetroEstadoDto,
  type LocacoesAccessMode,
  type MaquinarioDto,
  type RegistroHorimetroDto,
  type RegistroHorimetroFotoDto,
  type RegistroHorimetroTipo,
} from "@/services/locacoes";
import { getHorimetroContratoMaquina, resolveDefaultMaquinaId } from "./locacoesUtils";

interface ContratoContext {
  id: string;
  dataInicio: string;
  registros: RegistroHorimetroDto[];
}

interface FieldErrors {
  maquinaId?: string;
  horimetroInformado?: string;
  dataHora?: string;
  fotos?: string;
}

interface Props {
  open: boolean;
  editing: RegistroHorimetroDto | null;
  maquinarios: MaquinarioDto[];
  contratoContext?: ContratoContext | null;
  accessMode?: LocacoesAccessMode;
  busy?: boolean;
  onClose: () => void;
  onSave: (values: {
    maquinaId: string;
    tipo: RegistroHorimetroTipo;
    horimetroInformado: number;
    observacao: string;
    dataHora: string;
    contratoId?: string;
  }) => Promise<RegistroHorimetroDto | void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

function validateFields(
  values: {
    isNew: boolean;
    maquinaId: string;
    tipo: RegistroHorimetroTipo;
    horimetroInformado: string;
    dataHora: string;
    contratoDataInicio?: string;
    minHorimetro: number;
    minHorimetroFinal: number | null;
    totalPhotos: number;
    fotoObrigatoria: boolean;
  },
): FieldErrors {
  const errors: FieldErrors = {};
  if (values.isNew && !values.maquinaId) {
    errors.maquinaId = "Selecione a máquina.";
  }
  if (values.contratoDataInicio && values.dataHora) {
    const d = values.dataHora.slice(0, 10);
    if (d < values.contratoDataInicio) {
      errors.dataHora = `Data não pode ser anterior ao início do contrato (${values.contratoDataInicio.split("-").reverse().join("/")}).`;
    }
  }
  const h = Number(values.horimetroInformado);
  if (!values.horimetroInformado.trim() || !Number.isFinite(h) || h < 0) {
    errors.horimetroInformado = "Informe um horímetro válido.";
  } else if (values.tipo === "final" && values.minHorimetroFinal != null && h <= values.minHorimetroFinal) {
    errors.horimetroInformado = `Final deve ser maior que o Início (${values.minHorimetroFinal} h).`;
  } else if (values.tipo === "inicio" && h < values.minHorimetro) {
    errors.horimetroInformado = `Não pode ser menor que ${values.minHorimetro} h.`;
  } else if (values.tipo !== "final" && h < values.minHorimetro) {
    errors.horimetroInformado = `Não pode ser menor que ${values.minHorimetro} h.`;
  }
  if (values.fotoObrigatoria && values.totalPhotos < 1) {
    errors.fotos = "Anexe pelo menos uma foto do painel do horímetro.";
  }
  return errors;
}

export function ModalRegistroHorimetro({
  open,
  editing,
  maquinarios,
  contratoContext,
  busy,
  onClose,
  onSave,
  onDelete,
  onMutationComplete,
}: Props) {
  const [maquinaId, setMaquinaId] = useState("");
  const [tipo, setTipo] = useState<RegistroHorimetroTipo>("inicio");
  const [horimetroInformado, setHorimetroInformado] = useState("");
  const [observacao, setObservacao] = useState("");
  const [dataHora, setDataHora] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [fotos, setFotos] = useState<RegistroHorimetroFotoDto[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<Array<{ file: File; preview: string }>>([]);
  const [fotoBusy, setFotoBusy] = useState(false);
  const [pendingFotoDeleteId, setPendingFotoDeleteId] = useState<string | null>(null);
  const [estado, setEstado] = useState<HorimetroEstadoDto | null>(null);

  const isEditing = Boolean(editing);
  const selected = maquinarios.find((m) => m.id === maquinaId);
  const emContrato = Boolean(contratoContext);
  const recordId = editing?.id;

  const horimetroBaseContrato = useMemo(() => {
    if (!contratoContext || !maquinaId) return 0;
    return getHorimetroContratoMaquina(contratoContext, maquinaId, contratoContext.registros) ?? 0;
  }, [contratoContext, maquinaId]);

  const minHorimetro = editing
    ? editing.horimetroAtual
    : emContrato
      ? (estado?.horimetroBase ?? horimetroBaseContrato)
      : (estado?.horimetroBase ?? selected?.horimetroAtual ?? 0);

  /** Exibição informativa — maior horímetro registrado neste contrato. */
  const ultimoHorimetroContrato = emContrato ? horimetroBaseContrato : 0;

  const minHorimetroFinal =
    editing?.tipo === "final"
      ? editing.horimetroAtual
      : !isEditing && tipo === "final"
        ? (estado?.minHorimetroFinal ?? estado?.sessaoAberta?.horimetroInicio ?? null)
        : null;

  const tipoLocked = !isEditing && Boolean(maquinaId) && Boolean(estado);

  const totalPhotos = fotos.length + pendingPhotos.length;
  const fotoObrigatoria = true;

  const formSessionKey = editing?.id ?? "new";

  useModalFormSession(open, formSessionKey, () => {
    setFormError(null);
    setFieldErrors({});
    if (editing) {
      setMaquinaId(editing.maquinaId);
      setTipo(editing.tipo);
      setHorimetroInformado(String(editing.horimetroInformado));
      setObservacao(upperLocacaoText(editing.observacao));
      setDataHora(editing.dataHora.slice(0, 16));
      setFotos(editing.fotos ?? []);
      setPendingPhotos((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.preview));
        return [];
      });
    } else {
      const defaultId = resolveDefaultMaquinaId(maquinarios);
      setMaquinaId(defaultId);
      setTipo("inicio");
      setHorimetroInformado("");
      setObservacao("");
      setDataHora(new Date().toISOString().slice(0, 16));
      setPendingPhotos((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.preview));
        return [];
      });
      setFotos([]);
    }
    setEstado(null);
  });

  useEffect(() => {
    if (!open || isEditing || !maquinaId) {
      if (!maquinaId) setEstado(null);
      return;
    }
    let cancelled = false;
    void fetchHorimetroEstado(maquinaId, contratoContext?.id)
      .then((e) => {
        if (cancelled) return;
        setEstado(e);
        setTipo(e.proximoTipo);
        if (e.proximoTipo === "final" && e.sessaoAberta) {
          setHorimetroInformado((prev) => {
            if (prev.trim()) return prev;
            return String(Math.max(e.sessaoAberta!.horimetroInicio, e.horimetroBase));
          });
        } else {
          setHorimetroInformado((prev) => (prev.trim() ? prev : String(e.horimetroBase)));
        }
      })
      .catch(() => {
        if (!cancelled) setEstado(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEditing, maquinaId, contratoContext?.id]);

  useEffect(() => {
    if (!open || !editing?.fotos?.length) return;
    setFotos(editing.fotos);
  }, [open, editing?.id, editing?.fotos?.length]);

  useEffect(() => {
    if (selected && !editing && !horimetroInformado && !estado && !emContrato) {
      setHorimetroInformado(String(selected.horimetroAtual));
    }
  }, [selected?.id, editing, horimetroInformado, estado, emContrato, selected?.horimetroAtual]);

  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handlePickPhoto = (file: File) => {
    if (recordId) {
      void handleUploadFoto(file);
      return;
    }
    const preview = URL.createObjectURL(file);
    setPendingPhotos((prev) => [...prev, { file, preview }]);
    clearFieldError("fotos");
  };

  const handleRemovePendingPhoto = (index: number) => {
    setPendingPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return next;
    });
  };

  const handleUploadFoto = async (file: File) => {
    if (!recordId) return;
    setFotoBusy(true);
    setFormError(null);
    try {
      const foto = await uploadRegistroHorimetroFoto(recordId, file);
      setFotos((prev) => [...prev, foto]);
      clearFieldError("fotos");
      await onMutationComplete?.();
    } catch (e: unknown) {
      setFormError(extractApiError(e, "Erro ao enviar foto."));
    } finally {
      setFotoBusy(false);
    }
  };

  const handleDeleteFoto = async (fotoId: string) => {
    if (!recordId) return;
    setFotoBusy(true);
    setFormError(null);
    try {
      await deleteRegistroHorimetroFoto(recordId, fotoId);
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
      await onMutationComplete?.();
    } catch (e: unknown) {
      setFormError(extractApiError(e, "Erro ao excluir foto."));
    } finally {
      setFotoBusy(false);
    }
  };

  const requestDeleteFoto = (fotoId: string) => setPendingFotoDeleteId(fotoId);

  const handleSubmit = async () => {
    setFormError(null);
    const errors = validateFields({
      isNew: !isEditing,
      maquinaId,
      tipo,
      horimetroInformado,
      dataHora,
      contratoDataInicio: contratoContext?.dataInicio,
      minHorimetro,
      minHorimetroFinal,
      totalPhotos,
      fotoObrigatoria,
    });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError("Corrija os campos destacados antes de salvar.");
      return;
    }
    setFieldErrors({});

    const h = Number(horimetroInformado);
    try {
      const result = await onSave({
        maquinaId: editing?.maquinaId ?? maquinaId,
        tipo,
        horimetroInformado: h,
        observacao,
        dataHora: dataHora ? new Date(dataHora).toISOString() : new Date().toISOString(),
        ...(emContrato && contratoContext ? { contratoId: contratoContext.id } : {}),
      });

      const savedId = result?.id ?? recordId;
      if (savedId && pendingPhotos.length > 0) {
        setFotoBusy(true);
        try {
          for (const p of pendingPhotos) {
            await uploadRegistroHorimetroFoto(savedId, p.file);
          }
          pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
          setPendingPhotos([]);
        } finally {
          setFotoBusy(false);
        }
      }

      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      setFormError(extractApiError(e, "Erro ao salvar registro."));
    }
  };

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
        <Clock className="h-4 w-4 text-emerald-600" />
      </div>
      <span className="font-bold text-slate-900">
        {isEditing ? "Editar registro de horímetro" : "Novo registro de horímetro"}
      </span>
    </div>
  );

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {isEditing && onDelete && (
              <Button type="button" variant="secondary" onClick={onDelete} disabled={busy || fotoBusy}>
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy || fotoBusy}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} loading={busy || fotoBusy} disabled={fotoBusy}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{formError}</div>
        )}

        <p className="text-xs text-slate-500">
          Cada sessão exige Início e depois Final. Horas são contabilizadas no dia civil do Final.
          {fotoObrigatoria
            ? " Anexe ao menos uma foto do painel do horímetro (obrigatório)."
            : ""}
        </p>

        {!isEditing && estado?.sessaoAberta && (
          <div
            className={cn(
              "rounded-lg border px-3 py-2 text-xs",
              estado.proximoTipo === "final"
                ? "border-sky-200 bg-sky-50 text-sky-900"
                : "border-amber-200 bg-amber-50 text-amber-900",
            )}
          >
            {estado.proximoTipo === "final" ? (
              <>
                Sessão em aberto desde <strong>{estado.sessaoAberta.dataInicio}</strong> (horímetro inicial{" "}
                <strong>{estado.sessaoAberta.horimetroInicio} h</strong>). Preencha o registro de{" "}
                <strong>Final</strong> abaixo para encerrar a sessão.
              </>
            ) : (
              <>
                Operação aberta desde {estado.sessaoAberta.dataInicio} com horímetro inicial{" "}
                <strong>{estado.sessaoAberta.horimetroInicio} h</strong>. Registre o <strong>Final</strong> antes de
                um novo Início.
              </>
            )}
          </div>
        )}

        {isEditing ? (
          <Input label="Máquina" value={editing!.maquinaNome} disabled />
        ) : (
          <Select
            label="Máquina *"
            value={maquinaId}
            error={fieldErrors.maquinaId}
            onChange={(e) => {
              setMaquinaId(e.target.value);
              setHorimetroInformado("");
              clearFieldError("maquinaId");
            }}
            options={maquinarios.map((m) => ({ value: m.id, label: `${m.nome} (${m.codigoInterno || "—"})` }))}
            placeholder="Selecione…"
          />
        )}

        <Select
          label="Tipo de registro *"
          value={tipo}
          disabled={tipoLocked}
          onChange={(e) => setTipo(e.target.value as RegistroHorimetroTipo)}
          options={Object.entries(REGISTRO_HORIMETRO_LABELS).map(([value, label]) => ({ value, label }))}
        />

        {isEditing && (
          <p className="text-xs text-slate-500">
            Horímetro anterior ao registro: <strong>{editing!.horimetroAtual} h</strong>
          </p>
        )}
        {!isEditing && emContrato && selected && (
          <p className="text-xs text-slate-500">
            {ultimoHorimetroContrato > 0 ? (
              <>
                Último horímetro neste contrato: <strong>{ultimoHorimetroContrato} h</strong>
              </>
            ) : (
              <>Nenhum registro neste contrato — informe o horímetro inicial.</>
            )}
            {minHorimetro > 0 && minHorimetro !== ultimoHorimetroContrato && (
              <>
                {" "}
                • Horímetro da máquina: <strong>{minHorimetro} h</strong>
              </>
            )}
            {tipo === "final" && minHorimetroFinal != null && (
              <>
                {" "}
                • Início da sessão: <strong>{minHorimetroFinal} h</strong>
              </>
            )}
          </p>
        )}
        {!isEditing && !emContrato && selected && (
          <p className="text-xs text-slate-500">
            Horímetro atual da máquina: <strong>{selected.horimetroAtual} h</strong>
          </p>
        )}

        <Input
          label="Horímetro informado *"
          type="number"
          min={tipo === "final" && minHorimetroFinal != null ? minHorimetroFinal + 0.1 : minHorimetro}
          step="0.1"
          value={horimetroInformado}
          error={fieldErrors.horimetroInformado}
          onChange={(e) => {
            setHorimetroInformado(e.target.value);
            clearFieldError("horimetroInformado");
          }}
          placeholder={emContrato && horimetroBaseContrato === 0 ? "Ex.: 0" : undefined}
        />

        <Input
          label="Data/Hora"
          type="datetime-local"
          value={dataHora}
          min={contratoContext?.dataInicio ? `${contratoContext.dataInicio}T00:00` : undefined}
          error={fieldErrors.dataHora}
          onChange={(e) => {
            setDataHora(e.target.value);
            clearFieldError("dataHora");
          }}
        />

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observação</label>
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(upperLocacaoText(e.target.value))}
            rows={2}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>

        <div className="space-y-2">
          <label
            className={cn(
              "text-xs font-semibold uppercase tracking-wide",
              fieldErrors.fotos ? "text-red-600" : "text-slate-500",
            )}
          >
            Fotos do horímetro{fotoObrigatoria ? " *" : ""}
          </label>
          <p className="text-xs text-slate-500">
            {fotoObrigatoria
              ? "Anexe fotos do painel para conferência e evidência (obrigatório)."
              : "Opcional — anexe fotos do painel se desejar registrar evidência visual."}
          </p>
          {fieldErrors.fotos && <p className="text-xs font-medium text-red-500">{fieldErrors.fotos}</p>}

          <label
            className={cn(
              "inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-4 py-3 text-sm font-medium transition-colors",
              fieldErrors.fotos
                ? "border-red-300 bg-red-50/50 text-red-700 hover:border-red-400"
                : "border-slate-300 bg-slate-50 text-slate-700 hover:border-brand-300 hover:bg-brand-50/50",
              (fotoBusy || busy) && "pointer-events-none opacity-60",
            )}
          >
            <ImageIcon className="h-4 w-4 text-brand-600" />
            {fotoBusy ? "Enviando…" : "Adicionar foto"}
            <input
              type="file"
              className="hidden"
              accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
              disabled={fotoBusy || busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handlePickPhoto(f);
                e.target.value = "";
              }}
            />
          </label>

          {(pendingPhotos.length > 0 || fotos.length > 0) && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {fotos.map((foto) => (
                <div key={foto.id} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {recordId ? (
                    <a
                      href={registroHorimetroFotoUrl(recordId, foto.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-[4/3] bg-slate-100"
                    >
                      <img
                        src={registroHorimetroFotoUrl(recordId, foto.id)}
                        alt={foto.originalName}
                        className="h-full w-full object-cover"
                      />
                    </a>
                  ) : (
                    <div className="flex aspect-[4/3] items-center justify-center bg-slate-100 text-xs text-slate-500">
                      {foto.originalName}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <p className="min-w-0 truncate text-xs text-slate-600" title={foto.originalName}>
                      {foto.originalName}
                    </p>
                    {recordId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={fotoBusy}
                        onClick={() => requestDeleteFoto(foto.id)}
                      >
                        Excluir
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {pendingPhotos.map((p, i) => (
                <div key={`pending-${i}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <img src={p.preview} alt="" className="aspect-[4/3] w-full object-cover bg-slate-100" />
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <p className="min-w-0 truncate text-xs text-slate-600">{p.file.name}</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={fotoBusy}
                      onClick={() => handleRemovePendingPhoto(i)}
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>

    <ConfirmModal
      isOpen={pendingFotoDeleteId != null}
      title="Excluir foto"
      message="Excluir esta foto do registro?"
      confirmLabel="Excluir"
      onConfirm={async () => {
        if (!pendingFotoDeleteId) return;
        const fotoId = pendingFotoDeleteId;
        setPendingFotoDeleteId(null);
        await handleDeleteFoto(fotoId);
      }}
      onCancel={() => setPendingFotoDeleteId(null)}
      loading={fotoBusy}
    />
    </>
  );
}
